import React, {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {LayoutGrid, ReceiptText, ShoppingBag, ShoppingCart} from "lucide-react";
import {HeaderAction, PageHeader} from "../../stock/StockUI";
import {fetchMarketplaceCatalog} from "./marketplaceApi";
import useMarketplaceCart from "./useMarketplaceCart";
import MarketplaceCatalogTab from "./MarketplaceCatalogTab";
import MarketplaceCartTab from "./MarketplaceCartTab";
import MarketplaceOrdersTab from "./MarketplaceOrdersTab";

// Marketplace: matériel de caisse enregistreuse vendu par QuickCom — catalogue
// par catégorie, panier et commande.
const MarketplacePage = () => {
    const {data, isLoading, isError} = useQuery({
        queryKey: ["marketplace", "catalog"],
        queryFn: fetchMarketplaceCatalog,
        staleTime: 10 * 60 * 1000,
    });
    const cart = useMarketplaceCart();
    const [activeTab, setActiveTab] = useState("catalog");
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    const tabs = [
        {key: "catalog", label: "Catalogue", Icon: LayoutGrid},
        {key: "cart", label: "Panier", Icon: ShoppingCart, badge: cart.count || null},
        {key: "orders", label: "Mes commandes", Icon: ReceiptText},
    ];

    const addToCart = (product) => {
        cart.add(product);
        toast.success(`${product.name} ajouté au panier`);
    };

    // « Commander » on a product: put it in the cart (once) and open the order form.
    const orderNow = (product) => {
        if (!cart.lines.some((l) => l.product.id === product.id)) cart.add(product);
        setActiveTab("cart");
        setCheckoutOpen(true);
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <PageHeader
                title="Marketplace"
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                actions={activeTab === "catalog" ? (
                    <HeaderAction icon={ShoppingCart} label={`Panier (${cart.count})`} onClick={() => setActiveTab("cart")}/>
                ) : activeTab === "cart" && cart.count > 0 && (
                    <HeaderAction icon={ShoppingBag} label="Commander" onClick={() => setCheckoutOpen(true)}/>
                )}
            />

            {activeTab === "catalog" && (
                <MarketplaceCatalogTab
                    categories={data?.categories || []}
                    products={data?.products || []}
                    isLoading={isLoading}
                    isError={isError}
                    cart={cart}
                    onAdd={addToCart}
                    onOrder={orderNow}
                    onViewCart={() => setActiveTab("cart")}
                    onCheckout={() => {
                        setActiveTab("cart");
                        setCheckoutOpen(true);
                    }}
                />
            )}
            {activeTab === "cart" && (
                <MarketplaceCartTab
                    cart={cart}
                    checkoutOpen={checkoutOpen}
                    onCheckoutChange={setCheckoutOpen}
                    onBrowse={() => setActiveTab("catalog")}
                    onOrdered={() => setActiveTab("orders")}
                />
            )}
            {activeTab === "orders" && <MarketplaceOrdersTab onBrowse={() => setActiveTab("catalog")}/>}
        </div>
    );
};

export default MarketplacePage;
